<script setup lang="ts">
/**
 * Body of /resume-synonyms/<slug>.
 *
 * The intro first (why the word is weak and what to do instead), then one
 * block per sense: the synonyms that fit that situation, each with a note on
 * when, and the same bullet before and after. A thesaurus lists words; the
 * point of this page is the choice between them.
 */
import { findResumeSynonym, synonymWord, type ResumeSynonymEntry } from '~/constants/resumeSynonyms'

const props = defineProps<{
  entry: ResumeSynonymEntry
  word: string
  count: number
}>()

const { t, locale } = useI18n()

const related = computed(() =>
  props.entry.related
    .map(slug => findResumeSynonym(slug))
    .filter((item): item is NonNullable<typeof item> => Boolean(item)),
)
</script>

<template>
  <div class="sofax">
    <!-- The answer: why the word is weak, and the fix -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-sm">
        <div class="shell">
          <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
            <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
              {{ t('resumeSynonyms.page.shortLabel', { count }) }}
            </div>
            <p class="text-lg">{{ entry.intro }}</p>
          </div>

          <!-- Jump links: one per sense -->
          <nav class="mx-auto mt-8 max-w-3xl" :aria-label="t('resumeSynonyms.page.jumpLabel')">
            <ul class="flex flex-wrap gap-3">
              <li v-for="sense in entry.senses" :key="sense.id">
                <a :href="`#${sense.id}`" class="inline-block rounded-full border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-surface dark:border-gray-700 dark:text-white dark:hover:bg-gray-800">
                  {{ sense.label }}
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </section>

    <!-- One block per sense -->
    <section
      v-for="(sense, index) in entry.senses"
      :id="sense.id"
      :key="sense.id"
      :class="index % 2 === 0 ? 'surface-alt' : 'bg-white dark:bg-gray-950'"
    >
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto max-w-4xl">
            <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('resumeSynonyms.page.senseTitle', { word: word.toLowerCase(), sense: sense.label }) }}</h2>

            <ul class="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
              <li
                v-for="synonym in sense.synonyms"
                :key="synonym.word"
                class="rounded-[0.625rem] p-5"
                :class="index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-surface dark:bg-gray-900'"
              >
                <h3 class="!text-xl">{{ synonym.word }}</h3>
                <p class="mt-2 text-base">{{ synonym.note }}</p>
              </li>
            </ul>

            <div class="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div class="rounded-[0.625rem] p-5" :class="index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-surface dark:bg-gray-900'">
                <div class="mb-2 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
                  <i class="bi bi-x-circle shrink-0 text-lg text-muted" />
                  {{ t('templatesPage.intro.before') }}
                </div>
                <p class="text-base">{{ sense.example.before }}</p>
              </div>
              <div class="rounded-[0.625rem] bg-accent-soft/20 p-5 dark:bg-accent/10">
                <div class="mb-2 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
                  <i class="bi bi-check-circle-fill shrink-0 text-lg text-accent" />
                  {{ t('templatesPage.intro.after') }}
                </div>
                <p class="text-base">{{ sense.example.after }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Words to leave in the thesaurus, and the one tip -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto grid max-w-4xl grid-cols-1 gap-6" :class="entry.avoid ? 'md:grid-cols-2' : ''">
            <div v-if="entry.avoid" class="rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800">
              <h2 class="!text-2xl">{{ t('resumeSynonyms.page.avoidTitle') }}</h2>
              <p class="mt-3 text-base">{{ entry.avoid }}</p>
            </div>
            <div class="rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
              <h2 class="!text-2xl">{{ t('resumeSynonyms.page.tipTitle') }}</h2>
              <p class="mt-3 text-base">{{ entry.tip }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Related words, the hub, and the one offer -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto max-w-3xl text-center">
            <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('resumeSynonyms.page.relatedTitle') }}</h2>
            <ul class="mt-8 flex flex-wrap justify-center gap-3">
              <li v-for="item in related" :key="item.slug">
                <LocaleLink
                  :to="`/resume-synonyms/${item.slug}`"
                  class="inline-block rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink hover:bg-surface dark:bg-gray-800 dark:text-white"
                >
                  {{ t('resumeSynonyms.page.relatedLink', { word: synonymWord(item, locale) }) }}
                </LocaleLink>
              </li>
            </ul>
            <p class="mt-6">
              <LocaleLink to="/resume-synonyms" class="link">{{ t('resumeSynonyms.page.hubLink') }} →</LocaleLink>
            </p>
            <p class="mt-8 text-lg">{{ t('resumeSynonyms.page.ctaText') }}</p>
            <div class="mt-6">
              <SofaxBtn to="/free-cv-builder" :label="t('resumeSynonyms.page.cta')" variant="accent" />
            </div>
            <p class="mt-6">
              <LocaleLink to="/how-to-write-a-resume" class="link">{{ t('resumeSynonyms.page.guideLink') }} →</LocaleLink>
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
